import { PubSubService } from '../_shared/pubSubServiceInterface';

export type UserProfile = {
  /**
   * Per-user feature flags returned by the RIS profile endpoint. A feature is
   * available when its value is `true`, e.g. `{ favoritos: true }`.
   */
  features?: Record<string, boolean>;
  [key: string]: unknown;
};

/**
 * Holds the physician/user profile fetched from the RIS backend at viewer
 * startup (see `platform/app/src/routes/Mode/fetchUserProfile.ts`). Consumed for
 * conditional rendering of viewer features based on the profile's `features`
 * flags. Fork-specific — not part of upstream OHIF.
 */
class UserProfileService extends PubSubService {
  public static readonly EVENTS = {
    PROFILE_UPDATED: 'event::userProfile:updated',
  };

  public static REGISTRATION = {
    name: 'userProfileService',
    altName: 'UserProfileService',
    create: () => {
      return new UserProfileService();
    },
  };

  private profile: UserProfile | null = null;

  constructor() {
    super(UserProfileService.EVENTS);
  }

  public setProfile(profile: UserProfile | null) {
    this.profile = profile;
    this._broadcastEvent(UserProfileService.EVENTS.PROFILE_UPDATED, profile);
  }

  public getProfile() {
    return this.profile;
  }

  /** The `features` map from the profile, or an empty object when absent. */
  public getFeatures(): Record<string, boolean> {
    return this.profile?.features ?? {};
  }

  /** True when the named feature flag is explicitly enabled in the profile. */
  public hasFeature(feature: string): boolean {
    return this.getFeatures()[feature] === true;
  }

  public reset() {
    this.profile = null;
    super.reset();
  }
}

export default UserProfileService;
